import classNames from "classnames";
import PropTypes from "prop-types";
import Tier1Icon from "../../assets/loyalty-tier-1.svg";
import Tier2Icon from "../../assets/loyalty-tier-2.svg";
import Tier3Icon from "../../assets/loyalty-tier-3.svg";
import Tier4Icon from "../../assets/loyalty-tier-4.svg";
import Tick from "../../assets/tick.svg";
import styles from "./styles";
import global from "../../styles";
import Decoration from "../decoration";
import { formatHeaderWords } from "../../helpers/formatHeaderWords";
import { useLocaleProvider, useTranslate } from "../../context/locale-provider";
import { replaceJSX } from "../../utilities/replaceJSX";
import { formatPoints } from "../../helpers/formatPoints";

const iconArr = [Tier1Icon, Tier2Icon, Tier3Icon, Tier4Icon];

const birthdayDiscountArr = [];
let birthdayIndex = "";
let birthdayBenefitName = "";
const noBirthdayDiscountArr = [];

const HeaderElement = ({ Icon, pointsToUnlock, tierName, tierNumber }) => {
  return (
    <th scope="col">
      <div className={styles.headerElementOuter}>
        <div className={styles.tierHeaderWrapper}>
          <div className={styles.tierIcon}>
            {<Icon alt={`Tier ${tierNumber}`} />}
          </div>
          <div className={styles.tierName}>{tierName}</div>
        </div>
        <div className={styles.pointsToUnlock}>{pointsToUnlock}</div>
      </div>
    </th>
  );
};

const pointsSpend = (spendAmount, translate) => (
  <span>
    {translate("loyalty_landing_page_tiers_points_spend").replace(
      "%{spendAmount}",
      spendAmount
    )}
  </span>
);

const noPointsToUnlockHeading = (currencyCountry, spendAmount, translate) =>
  currencyCountry ? (
    <>
      <span className={styles.freeToJoinMobile}>
        {translate("loyalty_landing_page_benefits_table_free_header_mobile")}
      </span>
      <span className={styles.freeToJoinDesktop}>
        {translate("loyalty_landing_page_benefits_table_free_header_desktop")}
      </span>
    </>
  ) : (
    <>{pointsSpend(spendAmount, translate)}</>
  );

const pointsToUnlockHeading = (currencyCountry, spendAmount, translate) =>
  currencyCountry ? (
    <>
      <span className={styles.spendAmountMobile}>{spendAmount}</span>
      <span className={styles.spendAmountDesktop}>
        {translate(
          "loyalty_landing_page_benefits_table_spend_header_desktop"
        ).replace("%{spendAmount}", spendAmount)}
      </span>
    </>
  ) : (
    <>{pointsSpend(spendAmount, translate)}</>
  );

const displayPlus = (currencyCountry, tierName) =>
  currencyCountry || tierName === "A-lister" ? "+" : "";

const formatPointsToUnlock = (
  pointsToUnlock,
  translate,
  tierName,
  nextTierPointsToUnlock,
  country,
  currencyCountry
) => {
  const currency = window && window.asos.webContext.getBrowseCurrency();
  const spendAmount = `${formatPoints(
    country,
    currency,
    pointsToUnlock,
    currencyCountry,
    nextTierPointsToUnlock
  )}${displayPlus(currencyCountry, tierName)}`;
  return pointsToUnlock === 0 ? (
    <>{noPointsToUnlockHeading(currencyCountry, spendAmount, translate)}</>
  ) : (
    <>{pointsToUnlockHeading(currencyCountry, spendAmount, translate)}</>
  );
};

const BirthdayRow = ({ tiers, benefitName }) => {
  return (
    <tr>
      <th
        scope="row"
        className={classNames(styles.benefitName, "app_leavesden2")}
      >
        {benefitName}
      </th>
      {tiers.map((tier) => {
        const { cumulativeBenefitIds } = tier;
        let percentage;
        birthdayDiscountArr.forEach((birthdayBenefit) => {
          if (cumulativeBenefitIds.includes(birthdayBenefit.id)) {
            percentage = birthdayBenefit?.metadata?.discountPercentage;
            return;
          }
        });
        return (
          <td key={tier.id}>
            <div
              className={classNames(styles.tableImageDiv, global.london3)}
            >{`${percentage}%`}</div>
          </td>
        );
      })}
    </tr>
  );
};

const BenefitRow = ({ benefit, tiers }) => {
  return (
    <tr>
      <th
        scope="row"
        className={classNames(styles.benefitName, "app_leavesden2")}
      >
        {benefit.name}
      </th>
      {tiers.map((tier) => {
        return (
          <td key={tier.id}>
            {tier.cumulativeBenefitIds.includes(benefit.id) ? (
              <div className={styles.tableImageDiv}>
                <Tick alt="Check" />
              </div>
            ) : (
              ""
            )}
          </td>
        );
      })}
    </tr>
  );
};

export function removePercentage(text) {
  return text.replace(/\b\d{1,2}\s*%\s*/g, "").trim();
}

const TierInfo = ({ loyaltyData, assetsUrl }) => {
  const country = window && window.asos.webContext.getBrowseCountry();
  const currencyCountry = country === "GB";
  const { benefits = [], tiers } = loyaltyData;
  const { languageStrings, localisedLinks } = useLocaleProvider();

  const translate = useTranslate({ languageStrings });
  const header = translate("loyalty_landing_page_tiers_header");
  const headerFormatted = formatHeaderWords(header);

  benefits.forEach((benefit, index) => {
    if (benefit.type === "BirthdayDiscount") {
      birthdayBenefitName = removePercentage(benefit.name);
      if (!birthdayIndex) {
        birthdayIndex = index;
      }
      birthdayDiscountArr.push(benefit);
    } else {
      const notADuplicate = (benefitId) => {
        const index = noBirthdayDiscountArr.findIndex((benefit) => {
          return benefit.id === benefitId;
        });
        return index === -1;
      };
      if (notADuplicate(benefit.id)) {
        noBirthdayDiscountArr.push(benefit);
      }
    }
  });

  const TableHeader = ({ loyaltyData }) => {
    return loyaltyData.tiers.map((tier, index) => {
      const numericalTier = tier.id.replace(/[\D]/g, "");
      const Icon = iconArr[index];
      const nextTierPointsToUnlock =
        index < loyaltyData.tiers.length - 1
          ? loyaltyData.tiers[Number(numericalTier)].pointsToUnlock
          : "";

      return (
        <HeaderElement
          tierName={tier.name}
          Icon={Icon}
          key={Icon.toString()}
          pointsToUnlock={formatPointsToUnlock(
            tier.pointsToUnlock,
            translate,
            tier.name,
            nextTierPointsToUnlock,
            country,
            currencyCountry
          )}
          tierNumber={index + 1}
        />
      );
    });
  };

  const tableBody = noBirthdayDiscountArr.map((benefit) => {
    return <BenefitRow benefit={benefit} key={benefit.id} tiers={tiers} />;
  });

  return (
    <>
      <div className={classNames(styles.image)}>
        <img
          src={`${assetsUrl}/dWeb-Tiers_SS25_atrj9i`}
          alt="connect"
          width="100%"
          loading="lazy"
        />
        <div className={styles.container}>
          <Decoration
            width="32"
            height="150"
            x="0"
            y="20"
            className={global.mobileOnly}
          />
          <Decoration
            width="60"
            height="190"
            x="0"
            y="40"
            className={global.desktopAndTablet}
          />
          <Decoration
            width="80"
            height="15"
            x="0"
            y="-135"
            className={global.desktopAndTablet}
          />
          <Decoration
            width="165"
            height="15"
            x="0"
            y="-90"
            className={global.mobileOnly}
          />
          <div className={classNames(styles.header, global.headerASOSWorld)}>
            <h2 className={global.textBackground} aria-label={header}>
              {headerFormatted}
            </h2>
          </div>
          <div
            className={classNames(
              { [styles.pointsExplanation]: !currencyCountry },
              global.textBackground
            )}
          >
            {!currencyCountry && (
              <div className={classNames(global.leavesden1)}>
                {translate(
                  "loyalty_landing_page_benefits_table_spend_explanation"
                )}
              </div>
            )}
          </div>
          <div className={styles.tableContainer}>
            <table>
              <thead>
                <tr>
                  <th scope="col"></th>
                  {<TableHeader loyaltyData={loyaltyData} />}
                </tr>
              </thead>
              <tbody>
                {[
                  ...tableBody.slice(0, birthdayIndex),
                  <BirthdayRow
                    key={"birthday-benefit"}
                    tiers={tiers}
                    benefitName={birthdayBenefitName}
                  />,
                  ...tableBody.slice(birthdayIndex),
                ]}
              </tbody>
            </table>
          </div>
          <div className={classNames(global.leavesden3, styles.footerText)}>
            {replaceJSX(
              translate("loyalty_landing_page_benefits_table_disclaimer"),
              "%{termslink}",
              <a
                href={localisedLinks.termsAndConditions}
                className={styles.tsAndCsLink}
              >
                {translate("loyalty_landing_page_summary_terms_link_copy_web")}
              </a>
            )}
          </div>
        </div>
      </div>
      <img
        src={`${assetsUrl}/mWeb-Tiers_SS25_iaoiuw`}
        alt="connect"
        width="100%"
        loading="lazy"
        className={styles.mobileImage}
      />
    </>
  );
};

HeaderElement.propTypes = {
  Icon: PropTypes.object.isRequired,
  pointsToUnlock: PropTypes.string.isRequired,
  tierName: PropTypes.string.isRequired,
  tierNumber: PropTypes.string.isRequired,
};

BenefitRow.propTypes = {
  benefit: PropTypes.object.isRequired,
  tiers: PropTypes.array.isRequired,
};

BirthdayRow.propTypes = {
  tiers: PropTypes.array.isRequired,
  benefitName: PropTypes.string.isRequired,
};

TierInfo.propTypes = {
  loyaltyData: PropTypes.object.isRequired,
  assetsUrl: PropTypes.string.isRequired,
};

export default TierInfo;
